import { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import ToggleButtonSwitch from '../toggleButtonSwitch';
import CustomRadioGroup from '../customRadioGroup';
import SelectField from '../selectField';
import CustomCheckbox from '../customCheckbox';
import AlertDialog from '../alertDialog';
import { ReactComponent as WarningIcon } from '../../../assets/icons/warning-triangle-rounded.svg';
import { handleAnswerWeight, isArrayEmpty } from '../../../utility/helpers';
import { ATTRIBUTES } from '../../../constants/attributes';
import { getDialogTranslations } from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  answerStyle: {
    marginBottom: spacing(4),
  },
  titleText: {
    fontFamily: 'ProximaNova-Regular',
    marginBottom: spacing(3),
  },
  layoutType: {
    marginBottom: spacing(8),
  },
  horizontalMessageWarningWrap: {
    height: 44,
    display: 'flex',
    alignItems: 'center',
    background: primary.yellow3,
    borderRadius: 8,
    marginTop: spacing(-2),
    marginBottom: spacing(8),
  },
  warningIcon: {
    width: 20,
    height: 18,
    marginRight: spacing(4),
    marginLeft: spacing(4),
  },
  warningText: {
    lineHeight: '20px',
  },
  answerCount: {
    marginBottom: spacing(6),
  },
  selectContainer: {
    width: 80,
  },
  withNeutralAnswer: {
    marginBottom: spacing(6),
  },
  highlightedLabel: {
    fontFamily: 'ProximaNova-Bold',
  },
  disabledButton: {
    height: 32,
  },
}));

const DELAY = 1000;
const QUESTIONS_DEFAULT_NAME = 'questions';

const QuestionStyle = ({
  translations,
  fieldData,
  attribute,
  dialogs,
  isWarningOn,
  updateAttributeQuestions,
  setDialogVisibility,
  isEditDisabled,
}) => {
  const classes = useStyles();
  const { formLabels } = translations;
  const {
    fields: {
      layoutType,
      withAdditionalFeedback,
      withZeroValueAnswer,
      answersCount,
      withNeutralAnswer,
      withTextReplyQuestions,
    },
    fieldsStyleOptions: {
      increaseAnswersCount,
      decreaseAnswersCount,
      toOpenEndedQuestions,
      toMultipleChoiceQuestions,
      addNeutralAnswer,
      removeNeutralAnswer,
    },
  } = ATTRIBUTES;
  const { questions, text_reply_questions, answer_count, with_neutral_answer } =
    attribute;
  const { questionStyleChangeDialogOpened, answerTypeChangeDialogOpened } =
    dialogs;
  const {
    answerStyle,
    answerCount,
    layoutType: layoutTypeValue,
    neutralAnswer,
    zeroValueAnswer,
    additionalFeedback,
  } = fieldData;

  const [questionStyleData, setQuestionStyleData] = useState(null);
  const [field, setField] = useState(null);

  const isVertical = attribute.layout === ATTRIBUTES.verticalLayout;
  const hasDefinedAnswers = !attribute[withTextReplyQuestions];
  const displayWarning = hasDefinedAnswers && !isVertical && isWarningOn;

  const getDialogTranslationKey = () => {
    if (field?.name === withTextReplyQuestions) {
      return questionStyleData
        ? toOpenEndedQuestions
        : toMultipleChoiceQuestions;
    }

    if (field?.name === answersCount) {
      if (attribute.answer_count > questionStyleData) {
        return decreaseAnswersCount;
      }
      return increaseAnswersCount;
    }

    if (field?.name === withNeutralAnswer) {
      return questionStyleData ? addNeutralAnswer : removeNeutralAnswer;
    }
  };
  const translationKey = getDialogTranslationKey();
  const numberOfAnswersToDelete =
    translationKey === decreaseAnswersCount
      ? attribute.answer_count - questionStyleData
      : null;

  const handleQuestions = updatedValue => {
    if (text_reply_questions) {
      return questions;
    } else if (updatedValue) {
      return questions.map(question => {
        return {
          ...question,
          answers: question.answers.map(answer => ({
            text: answer.text,
          })),
        };
      });
    }
    return questions.map(question => {
      const answers = question.answers.map(answer => ({
        text: answer.text,
      }));
      answers.pop();
      return {
        ...question,
        answers,
      };
    });
  };

  const updateAttributeWithZeroValue = (name, updatedData) => {
    let questionsToCopy = handleQuestions(!attribute[withNeutralAnswer]);
    questionsToCopy = handleAnswerWeight(questionsToCopy, {
      ...attribute,
      ...updatedData,
    });
    updateAttributeQuestions([name, QUESTIONS_DEFAULT_NAME], {
      ...attribute,
      ...updatedData,
      questions: questionsToCopy,
    });
  };

  const handleChangeAttributeStyle = (currentField, updatedValue) => {
    const { name } = currentField;

    if (attribute[name] === updatedValue) return;

    const updatedData = { [name]: updatedValue };
    if (
      isArrayEmpty(attribute.questions) ||
      name === layoutType ||
      name === withAdditionalFeedback ||
      name === withZeroValueAnswer
    ) {
      if (currentField.dependant) {
        updatedData[currentField.dependant.name] = updatedValue
          ? currentField.dependant.resetValue
          : currentField.dependant.defaultValue;
      }
      if (name === withZeroValueAnswer) {
        return updateAttributeWithZeroValue(name, updatedData);
      }

      return updateAttributeQuestions([name], updatedData);
    }

    setField(currentField);
    setQuestionStyleData(updatedValue);
    setDialogVisibility({
      dialogName: 'questionStyleChangeDialog',
      opened: true,
    });
  };

  const getQuestions = () => {
    if (field.name === answersCount) {
      if (answer_count > questionStyleData) {
        return questions.map(question => {
          return {
            ...question,
            answer_count: questionStyleData,
            answers: question.answers
              .slice(0, questionStyleData)
              .map(answer => ({ text: answer.text })),
          };
        });
      }
      const populateAdditionalAnswers = Array.from(
        { length: questionStyleData - answer_count },
        (_, i) => ({
          text: `${answer_count + 1 + i}`,
        })
      );

      return questions.map(question => {
        if (with_neutral_answer) {
          question.answers.pop();
        }
        return {
          ...question,
          answer_count: questionStyleData,
          answers: question.answers
            .map(answer => ({ text: answer.text }))
            .concat(populateAdditionalAnswers),
        };
      });
    }

    if ([withNeutralAnswer, withZeroValueAnswer].includes(field.name)) {
      return handleQuestions(questionStyleData);
    }

    if (field.name === withTextReplyQuestions) {
      if (questionStyleData) {
        return questions.map(question => {
          return {
            ...question,
            answers: [],
            answer_count: 0,
          };
        });
      }
      return questions.map(question => {
        return {
          ...question,
          answer_count,
          answers: Array.from({ length: answer_count }, (_, i) => ({
            text: `${1 + i}`,
          })),
        };
      });
    }
  };

  const handleQuestionStyleConfirmation = async () => {
    const updatedData = {
      [field.name]: questionStyleData,
    };

    if (field.dependant) {
      updatedData[field.dependant.name] = questionStyleData
        ? field.dependant.resetValue
        : field.dependant.defaultValue;
    }

    if (!isArrayEmpty(questions)) {
      let questionsToCopy = getQuestions(field.name);
      questionsToCopy = handleAnswerWeight(questionsToCopy, {
        ...attribute,
        ...updatedData,
      });
      updateAttributeQuestions([field.name, QUESTIONS_DEFAULT_NAME], {
        ...attribute,
        ...updatedData,
        questions: questionsToCopy,
      });
      setField(null);
      setQuestionStyleData(null);
      setDialogVisibility({
        dialogName: 'questionStyleChangeDialog',
        opened: false,
      });
    }
  };

  const handleQuestionStyleClose = () => {
    setField(null);
    setQuestionStyleData(null);
    setDialogVisibility({
      dialogName: 'questionStyleChangeDialog',
      opened: false,
    });
  };

  const handleAnswerTypeChangeDialog = isOpened => {
    setDialogVisibility({
      dialogName: 'answerTypeChangeDialog',
      opened: isOpened,
    });
  };

  const handleAnswerTypeChangeOpen = newValue => {
    const isHorizontal = ATTRIBUTES.horizontalLayout === newValue;

    handleChangeAttributeStyle(layoutTypeValue, newValue);
    if (isHorizontal && !isArrayEmpty(questions)) {
      handleAnswerTypeChangeDialog(true);
    }
  };

  return (
    <>
      <div key={fieldData.name}>
        <Typography variant="h4" className={classes.titleText}>
          {formLabels[fieldData.title.translationKey].label}
        </Typography>
        <ToggleButtonSwitch
          className={classes.answerStyle}
          customButtonClass={classes.disabledButton}
          value={attribute[answerStyle.name]}
          items={answerStyle.items}
          isDisabled={isEditDisabled}
          onSelect={newValue =>
            handleChangeAttributeStyle(answerStyle, newValue)
          }
          isFullWidth
        />
        {hasDefinedAnswers && (
          <SelectField
            className={classes.answerCount}
            selectContainerClass={classes.selectContainer}
            labelClass={classes.highlightedLabel}
            label={formLabels[answerCount.translationKey].label}
            labelHelp={formLabels[answerCount.translationKey]?.labelHelp}
            name={answerCount.name}
            options={answerCount.options || []}
            parser={answerCount.parser}
            value={attribute[answerCount.name]}
            onChange={newValue =>
              handleChangeAttributeStyle(answerCount, newValue)
            }
            isDisabled={isEditDisabled}
            isOptionValueCentered
            isFullWidth
            isSearchDisabled
          />
        )}
        <div className={classes.withNeutralAnswer}>
          <CustomCheckbox
            isChecked={attribute[additionalFeedback.name]}
            label={formLabels[additionalFeedback.translationKey].fieldLabel}
            labelText={formLabels[additionalFeedback.translationKey].label}
            onChange={newValue =>
              handleChangeAttributeStyle(additionalFeedback, newValue)
            }
            disabled={isEditDisabled}
            isHighlightedLabel
            smallText
            isControlled
          />
        </div>
        <div className={classes.withNeutralAnswer}>
          <CustomCheckbox
            isChecked={attribute[neutralAnswer.name]}
            label={formLabels[neutralAnswer.translationKey].fieldLabel}
            labelText={formLabels[neutralAnswer.translationKey].label}
            onChange={newValue =>
              handleChangeAttributeStyle(neutralAnswer, newValue)
            }
            disabled={isEditDisabled}
            isHighlightedLabel
            smallText
            isControlled
          />
        </div>
        {hasDefinedAnswers && (
          <div className={classes.withNeutralAnswer}>
            <CustomCheckbox
              isChecked={attribute[zeroValueAnswer.name]}
              label={formLabels[zeroValueAnswer.translationKey].fieldLabel}
              labelText={formLabels[zeroValueAnswer.translationKey].label}
              onChange={newValue =>
                handleChangeAttributeStyle(zeroValueAnswer, newValue)
              }
              disabled={isEditDisabled}
              labelHelp={formLabels[zeroValueAnswer.translationKey].labelHelp}
              isHighlightedLabel
              smallText
              isControlled
            />
          </div>
        )}
        {hasDefinedAnswers && (
          <CustomRadioGroup
            className={classes.layoutType}
            label={formLabels[layoutTypeValue.name].label}
            value={attribute[layoutTypeValue.name]}
            isDisabled={isEditDisabled}
            options={layoutTypeValue.options}
            onChange={newValue => handleAnswerTypeChangeOpen(newValue)}
          />
        )}
        {displayWarning && (
          <Fade in={displayWarning} timeout={DELAY}>
            <div className={classes.horizontalMessageWarningWrap}>
              <WarningIcon className={classes.warningIcon} />
              <Typography className={classes.warningText}>
                {translations.horizontalMessageWarning}
              </Typography>
            </div>
          </Fade>
        )}
      </div>
      <AlertDialog
        translations={getDialogTranslations(
          translations.questionsForm.questionStyleChangeDialog,
          translationKey,
          numberOfAnswersToDelete
        )}
        isOpened={questionStyleChangeDialogOpened}
        onClose={handleQuestionStyleClose}
        onConfirm={handleQuestionStyleConfirmation}
      />
      <AlertDialog
        isOpened={answerTypeChangeDialogOpened}
        translations={
          translations.questionsForm.questionStyleChangeDialog
            .answerTypeChangeDialog
        }
        onClose={() => handleAnswerTypeChangeDialog(false)}
      />
    </>
  );
};

QuestionStyle.propTypes = {
  translations: PropTypes.object.isRequired,
  fieldData: PropTypes.object.isRequired,
  attribute: PropTypes.object.isRequired,
  setDialogVisibility: PropTypes.func.isRequired,
  dialogs: PropTypes.object.isRequired,
  isWarningOn: PropTypes.bool.isRequired,
  updateAttributeQuestions: PropTypes.func.isRequired,
  isEditDisabled: PropTypes.bool.isRequired,
};

export default QuestionStyle;
